import _ from 'lodash';
import React from 'react';
import DOMPurify from 'dompurify';

export function isPromise(promise) {
	return promise ? _.isFunction(promise.finally) : false;
}

export const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

const countryCodes = [
	'ad',
	'ae',
	'af',
	'ag',
	'ai',
	'al',
	'am',
	'ao',
	'aq',
	'ar',
	'as',
	'at',
	'au',
	'aw',
	'ax',
	'az',
	'ba',
	'bb',
	'bd',
	'be',
	'bf',
	'bg',
	'bh',
	'bi',
	'bj',
	'bl',
	'bm',
	'bn',
	'bo',
	'bq',
	'br',
	'bs',
	'bt',
	'bv',
	'bw',
	'by',
	'bz',
	'ca',
	'cc',
	'cd',
	'cf',
	'cg',
	'ch',
	'ci',
	'ck',
	'cl',
	'cm',
	'cn',
	'co',
	'cr',
	'cu',
	'cv',
	'cw',
	'cx',
	'cy',
	'cz',
	'de',
	'dj',
	'dk',
	'dm',
	'do',
	'dz',
	'ec',
	'ee',
	'eg',
	'eh',
	'er',
	'es',
	'et',
	'eu',
	'fi',
	'fj',
	'fk',
	'fm',
	'fo',
	'fr',
	'ga',
	'gb-eng',
	'gb-nir',
	'gb-sct',
	'gb-wls',
	'gb',
	'gd',
	'ge',
	'gf',
	'gg',
	'gh',
	'gi',
	'gl',
	'gm',
	'gn',
	'gp',
	'gq',
	'gr',
	'gs',
	'gt',
	'gu',
	'gw',
	'gy',
	'hk',
	'hm',
	'hn',
	'hr',
	'ht',
	'hu',
	'id',
	'ie',
	'il',
	'im',
	'in',
	'io',
	'iq',
	'ir',
	'is',
	'it',
	'je',
	'jm',
	'jo',
	'jp',
	'ke',
	'kg',
	'kh',
	'ki',
	'km',
	'kn',
	'kp',
	'kr',
	'kw',
	'ky',
	'kz',
	'la',
	'lb',
	'lc',
	'li',
	'lk',
	'lr',
	'ls',
	'lt',
	'lu',
	'lv',
	'ly',
	'ma',
	'mc',
	'md',
	'me',
	'mf',
	'mg',
	'mh',
	'mk',
	'ml',
	'mm',
	'mn',
	'mo',
	'mp',
	'mq',
	'mr',
	'ms',
	'mt',
	'mu',
	'mv',
	'mw',
	'mx',
	'my',
	'mz',
	'na',
	'nc',
	'ne',
	'nf',
	'ng',
	'ni',
	'nl',
	'no',
	'np',
	'nr',
	'nu',
	'nz',
	'om',
	'pa',
	'pe',
	'pf',
	'pg',
	'ph',
	'pk',
	'pl',
	'pm',
	'pn',
	'pr',
	'ps',
	'pt',
	'pw',
	'py',
	'qa',
	're',
	'ro',
	'rs',
	'ru',
	'rw',
	'sa',
	'sb',
	'sc',
	'sd',
	'se',
	'sg',
	'sh',
	'si',
	'sj',
	'sk',
	'sl',
	'sm',
	'sn',
	'so',
	'sr',
	'ss',
	'st',
	'sv',
	'sx',
	'sy',
	'sz',
	'tc',
	'td',
	'tf',
	'tg',
	'th',
	'tj',
	'tk',
	'tl',
	'tm',
	'tn',
	'to',
	'tr',
	'tt',
	'tv',
	'tw',
	'tz',
	'ua',
	'ug',
	'um',
	'un',
	'unknown',
	'us',
	'uy',
	'uz',
	'va',
	'vc',
	've',
	'vg',
	'vi',
	'vn',
	'vu',
	'wf',
	'ws',
	'ye',
	'yt',
	'za',
	'zm',
	'zw'
];

export const formatInternationalCurrency = (currencyCode, amount, type = 'compact') => {
	const isCompact = type === 'compact';
	const numberParts = new Intl.NumberFormat('en-US', {
		notation: type,
		maximumFractionDigits: 2,
		minimumFractionDigits: isCompact ? 0 : 2
	}).formatToParts(amount);
	const currencyParts = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currencyCode,
		currencyDisplay: 'symbol'
	}).formatToParts(amount);
	const currencyPart = _.find(currencyParts, { type: 'currency' });
	const isCurrencyCode = currencyPart?.value === currencyCode;
	const sign = _
		.filter(numberParts, ({ type }) => _.includes(['minusSign', 'plusSign'], type))
		.map(({ value }) => value)
		.join('');
	const number = _
		.filter(numberParts, ({ type }) => !_.includes(['minusSign', 'plusSign', 'literal'], type))
		.map(({ type, value }) => (type === 'compact' ? value.trim() : value))
		.join('');

	if (!currencyPart) {
		return `${sign}${number}`;
	}

	if (isCurrencyCode) {
		return `${sign}${number} ${currencyPart.value}`;
	}

	return `${sign}${currencyPart.value}${number}`;
}

export const simpleMarkdownToHtml = (text) => {
    if (!text) return '';
    const html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br/>');
    return DOMPurify.sanitize(html);
};

export const trafficSourcesTitles = {
	ADVERTISING: 'The viewer was referred to the video by an advertisement.',
	ANNOTATION: 'Viewers reached the video by clicking on an annotation in another video.',
	CAMPAIGN_CARD:
		'Views originated from claimed, user-uploaded videos that the content owner used to promote the viewed content.',
	END_SCREEN: ' The views were referred from the end screen of another video.',
	EXT_URL: 'The video views were referred from a link on another website.',
	NO_LINK_EMBEDDED: 'The video was embedded on another website when it was viewed.',
	NO_LINK_OTHER: 'YouTube did not identify a referrer for the traffic.',
	NOTIFICATION: 'The video views were referred from an email or notification from YouTube.',
	PLAYLIST: 'The video views occurred while the video was being played as part of a playlist.',
	PROMOTED: 'The video views were referred from an unpaid YouTube promotion.',
	RELATED_VIDEO: 'The video views were referred from a related video listing on another video watch page.',
	SUBSCRIBER:
		'The video views were referred from feeds on the YouTube homepage or from YouTube subscription features.',
	YT_CHANNEL: 'The video views occurred on a channel page.',
	YT_OTHER_PAGE:
		'The video views were referred from a link other than a search result or related video link that appeared on a YouTube page.',
	YT_PLAYLIST_PAGE: 'The video views originated from a page that lists all of the videos in a playlist.',
	YT_SEARCH: 'The video views were referred from YouTube search results.'
};

export function countryImage(countryCode) {
	if (_.isString(countryCode)) {
		const lowered = countryCode.toLowerCase();
		if (_.includes(countryCodes, lowered)) {
			return `https://viewiq-ui-assets.s3.amazonaws.com/flags/${lowered}.svg`;
		}
	}
	return 'https://viewiq-ui-assets.s3.amazonaws.com/flags/unknown.svg';
}

export function generateAbbreviation(string) {
	if (!_.isString(string)) {
		return '';
	}
	return string
		.match(/\b([a-z])/gi)
		.join('')
		.toUpperCase();
}

export function areComponentsEqual(component1, component2) {
	// Simple shallow comparison for React Refresh compatibility
	return component1 === component2;
}

export function stringifyFileSize(number) {
	const oneKB = 1024;
	const oneMB = oneKB * oneKB;
	if (number < oneKB) {
		return number + 'bytes';
	} else if (number > oneKB && number < oneMB) {
		return (number / oneKB).toFixed(1) + 'KB';
	} else if (number > oneMB) {
		return (number / oneMB).toFixed(1) + 'MB';
	}
}

export function imageBase64ToBlob(img) {
	const data = atob(img.substring('data:image/jpeg;base64,'.length));
	const asArray = new Uint8Array(data.length);

	for (let i = 0, len = data.length; i < len; ++i) {
		asArray[i] = data.charCodeAt(i);
	}

	return new Blob([asArray.buffer], { type: 'image/png' });
}

export function sliceCountryData(data) {
	var keysSorted = Object.keys(data).sort(function (a, b) {
		return data[b] - data[a];
	});

	var sliced_data = keysSorted.slice(0, 10).map((key) => ({
		code: key,
		title: key,
		value: data[key]
	}));
	return sliced_data;
}

export const checkFileExtension = (filename, extension) => {
	return filename.split('.').pop() === extension;
};

export const checkCSVFile = (file) => {
	return file?.name?.split('.')?.pop() === 'csv';
};

export const checkIconFile = (file) => {
	const extension = file?.name?.split('.').pop();
	return extension === 'jpeg' || extension === 'jpg' || extension === 'png';
}

export const populateComscoreNodes = (treeData, nodeIDs) => {
	if (_.isEmpty(treeData) || _.isEmpty(nodeIDs)) return [];
	const nodesArray = [];
	const checkSelectedNodes = (node) => {
		_.each(node.children, (child) => {
			if (_.includes(nodeIDs, child.id)) nodesArray.push(child);
			else if (!_.isEmpty(child.children)) checkSelectedNodes(child);
		});
	};
	_.each(treeData['us'], (node) => {
		if (_.includes(nodeIDs, node.id)) nodesArray.push(node);
		else if (!_.isEmpty(node.children)) checkSelectedNodes(node);
	});
	return nodesArray;
};

export const createComscoreTreeStrings = (treeData, region) => {
	if (_.isEmpty(treeData)) return {};
	const stringsList = {};
	_.each(treeData[region], (nodeOne) => {
		// nodeOne (viewwing behaviours, finance, etc) is not a selectable node hence not added to the search
		if (!_.isEmpty(nodeOne.children)) {
			_.each(nodeOne.children, (nodeTwo) => {
				stringsList[`${nodeOne.value} > ${nodeTwo.value}`] = {
					value: nodeTwo.value,
					id: nodeTwo.id,
					parentNodes: [nodeOne]
				};
				if (!_.isEmpty(nodeTwo.children)) {
					_.each(nodeTwo.children, (nodeThree) => {
						stringsList[`${nodeOne.value} > ${nodeTwo.value} > ${nodeThree.value}`] = {
							value: nodeThree.value,
							id: nodeThree.id,
							parentNodes: [nodeOne, nodeTwo]
						};
						if (!_.isEmpty(nodeThree.children)) {
							_.each(nodeThree.children, (nodeFour) => {
								stringsList[
									`${nodeOne.value} > ${nodeTwo.value} > ${nodeThree.value} > ${nodeFour.value}`
								] = {
									value: nodeFour.value,
									id: nodeFour.id,
									parentNodes: [nodeOne, nodeTwo, nodeThree]
								};
							});
						}
					});
				}
			});
		}
	});
	return stringsList;
};

export const checkPureObject = (obj) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null;

export const isNumeric = (value) => /^-?\d+$/.test(value);

// Build methods
import { Segment } from '../models/SegmentModel';
import { formatBigNumber, thousandsSeparated } from './numbers';

export const findPresetFilter = (steps, paramKey) => {
	let keyFilter = null;
	_.each(steps, (stepValue) => {
		_.each(stepValue.filters, (filterValue) => {
			let keyToCheck = filterValue.paramsID;
			if (_.isArray(filterValue.paramsID)) {
				keyToCheck = filterValue.paramsID[0];
			}
			if (keyToCheck == paramKey) {
				keyFilter = filterValue;
			} else if (filterValue.filters) {
				_.each(filterValue.filters, (childFilterValue) => {
					if (childFilterValue.paramsID == paramKey) {
						keyFilter = filterValue;
					}
				});
			} else if (keyToCheck==paramKey) {
				keyFilter = filterValue;
			}
		});
	});
	return keyFilter;
}

export const summaryGarmMapping = (selections, options, exclusionType) => {
	const advancedSuitabilityStrings = {
		1: 'Allow Any Suitable Content',
		2: 'Allow Low Suitability Content',
		3: 'Allow Medium Suitability Content',
		4: 'Allow Only Suitable Content'
	};
	const mapping = (minValue) => {
		if (minValue < 7) {
			return advancedSuitabilityStrings[1];
		} else if (minValue < 8){
			return advancedSuitabilityStrings[2];
		} else if (minValue < 9) {
			return advancedSuitabilityStrings[3];
		}
		return advancedSuitabilityStrings[4];
	}
	const exclusionMapping = (maxValue) => {
		return maxValue >= 9 ? advancedSuitabilityStrings[4] : maxValue >= 8 ? advancedSuitabilityStrings[3] : maxValue >= 7 ? advancedSuitabilityStrings[2] : advancedSuitabilityStrings[1];
	}
	if (!_.isPlainObject(selections)) {
		let [min, max] = [0,0];
		if (_.includes(selections, ',')) {
			[min, max] = selections.split(',');
		} else {
			[min, max] = [selections, selections];
		}
		return exclusionType ? exclusionMapping(max) : mapping(min);
	}
	return _.map(selections, (minMax, id) => {
		const advancedCategory = _.find(options, opt => String(opt.id) === String(id));
		const [min, max] = minMax.split(',');
		const suitability = exclusionType ? exclusionMapping(max) : mapping(min);
		return { id: _.random(0, 100), title: advancedCategory?.name || 'Not Available', description: suitability };
	});
}

export const setIabCategoryParam = (listParams, toggleStates, jsonParams, key) => {
	const usePrimary = listParams?.['relevant_primary_categories'];
	const toggleState = toggleStates?.['iab_categories'];
	const isThreeTwo = _.includes(toggleState, '3_0_2');
	const isThree = !isThreeTwo && _.includes(toggleState, '_3');
	const has302Data = !_.isEmpty(listParams?.['content_categories_3_0_2']) || !_.isEmpty(listParams?.['primary_content_categories_3_0_2']) || !_.isEmpty(listParams?.['exclude_content_categories_3_0_2']);
	const useThreeTwo = isThreeTwo && has302Data;
	if (useThreeTwo) {
		if (!_.includes(key, '3_0_2')) return;
	} else if (isThreeTwo) {
		if (_.includes(key, '3_0_2') || !_.includes(key, '3')) return;
	} else if (isThree) {
		if (_.includes(key, '3_0_2') || !_.includes(key, '_3')) return;
	} else if (_.includes(key, '3')) return;
	const value = listParams?.[key];
	let setParam = false;
	if (!_.isUndefined(value)) {
		if (_.includes(key, 'primary')) {
			if (usePrimary && key !== 'relevant_primary_categories') {
				setParam = true;
				if (_.includes(key, '3') && !useThreeTwo){
					setJsonParamV2(toggleState.replace('3_0_2', '3'), value, jsonParams);
				}
				else setJsonParamV2(toggleState, value, jsonParams);
			}
		} else {
			setParam = true;
			setJsonParamV2(key, value, jsonParams);
		}
	}
	if (usePrimary && setParam) {
		const primaryKey = useThreeTwo ? 'relevant_primary_categories_3_0_2' : (_.includes(key, '3') && isThreeTwo) || isThree ? 'relevant_primary_categories_3' : 'relevant_primary_categories';
		setJsonParamV2(primaryKey, true, jsonParams);
	}
}

export const findSummaryFilter = (filter) => {
	const results = [];
	const findSummaryFilterRec = (node) => {
		if (!node || typeof node !== 'object') return null;

		if (node.summaryItem) {
			results.push(node);
		}

		if (_.isArray(node.filters)&&!node.subSummaryList) {
			_.forEach(node.filters, child => findSummaryFilterRec(child));
		}
	};
	findSummaryFilterRec(filter);
	return results;
};

export const findSectionSelectionFilter = (filter) => {
	const results = [];
	const findSummaryFilterRec = (node) => {
		if (!node || typeof node !== 'object') return null;

		if (node.summaryItem&&!node.subSummaryList) {
			results.push(node);
		}

		if (_.isArray(node.filters)) {
			_.forEach(node.filters, child => findSummaryFilterRec(child));
		}
	};
	findSummaryFilterRec(filter);
	return results;
}

export const findPresetFilterV3 = (preset, buildFilters, paramsID) => {
	let isSuitability = ['advanced_suitability_categories', 'score_threshold', 'nlp_suitability_categories'].includes(paramsID);
	let resultFilter = [];
	_.find(preset, section => {
		return _.find(section.filters, filter => {
			let summaryFilters = findSectionSelectionFilter(buildFilters[filter]);
			if (summaryFilters?.[0]?.paramsID === paramsID || (isSuitability && summaryFilters?.[0]?.paramsID === 'brand_suitability')) {
				resultFilter = summaryFilters;
				return true;
			} else if ( summaryFilters?.[0]?.toggleOptions) {
				let toggles = summaryFilters[0].toggles;
				if (_.includes(paramsID,toggles[0].toggleID) || _.includes(paramsID,toggles[1].toggleID)) {
					resultFilter = summaryFilters;
					return true;
				}
			} else if (_.some(summaryFilters, sumFilt => sumFilt.paramsID == paramsID)) {
				resultFilter = summaryFilters;
				return true;
			} else if (_.some(summaryFilters, sumFilt => sumFilt.secondaryParamsID == paramsID)) {
				resultFilter = summaryFilters;
				return true;
			}
		});
	});
	return resultFilter || null;
}

export const deduplicateErrorMessages = (data, seen = new Set()) => {
	if (Array.isArray(data)) {
		data.forEach(item => deduplicateErrorMessages(item, seen));
	} else if (typeof data === 'object' && data !== null) {
		Object.values(data).forEach(value => deduplicateErrorMessages(value, seen));
	} else if (typeof data === 'string') {
		seen.add(data);
	}

	return seen;
}

export const onMapComscoreNames = (value, filterOptions) => {
	let updatedComscore = JSON.parse(JSON.stringify(value));
	let options = JSON.parse(JSON.stringify(filterOptions['comscoreTree']));
	const mapComscoreRec = (value, options) => {
		for (const key in options) {
			const v = options[key];
			if (value == key) {
				v['selected'] = true;
				return v.value;
			}
			if (v.children) {
				const res = mapComscoreRec(value, v.children);
				if (res) {
					v['selectedChildren'] = true;
					return res;
				}
			}
		}
		return null;
	}
	const comscoreStrings = filterOptions.comscoreStrings || {};
	_.each(updatedComscore, val => {
		let selectedOptions = options[val.region];
		const regionStrings = comscoreStrings[val.region] || {};
		const idToFullPath = _.reduce(regionStrings, (acc, data, fullPath) => {
			acc[data.id] = fullPath;
			return acc;
		}, {});
		val.ids =_.reduce(val.ids, (acc, v) => {
			let name = mapComscoreRec(v, selectedOptions);
			if (!_.isNull(name) && !_.isUndefined(name)) {
				const fullPath = idToFullPath[v] || _.find(idToFullPath, (_, id) => id == v) || name;
				acc.push({ id: String(v), name: fullPath });
			}
			return acc;
		}, []);
		options[val.region] = selectedOptions;
	});

	updatedComscore = _.filter(updatedComscore, val => !_.isEmpty(val.ids));
	if (!_.isEmpty(updatedComscore)) {
		updatedComscore.treeStructure = options;
		return updatedComscore;
	}
	return [];
}

export const onMapSummaryItems = (filter, listParams, toggleStates, filterOptions) => {
	const { optionsID, optionsIDKey, optionsTitleKey, title, summaryTitle, summaryType, summaryDisplayItem, paramsID, summaryOptionsFlat, summaryFlatParentID, summaryFlatChildID, summaryParamsID, toggleOptions, toggles, summaryToggle, summaryOptionsID, filters, subSummaryList } = filter;
	let listParamsID = paramsID;
	if (summaryToggle) {
		listParamsID = toggleStates[toggles[0].toggleParent];
		let toggleState = toggleStates[toggles[0].toggleParent];
		if (_.includes(paramsID, 'exclude')) {
			listParamsID = `exclude_${listParamsID}`;
		}
		if (_.includes(toggleState, '3_0_2')) {
			if (_.isEmpty(listParams[listParamsID]) && !_.isEmpty(listParams[listParamsID.replace('3_0_2', '3')])) listParamsID = listParamsID.replace('3_0_2', '3');

		} else if (summaryParamsID) {
			listParamsID = summaryParamsID;
		}
		if (_.isEmpty(listParams[listParamsID]) && paramsID=='brand_suitability') listParamsID = 'score_threshold';
	} else if (summaryParamsID) {
		let toggleState = toggleStates[toggles[0].toggleParent];
		if (_.includes(toggleState, '3_0_2')) listParamsID = _.isEmpty(listParams[`${summaryParamsID}_3_0_2`]) && !_.isEmpty(listParams[`${summaryParamsID}_3`]) ? `${summaryParamsID}_3` : `${summaryParamsID}_3_0_2`;
		else listParamsID = summaryParamsID;
	}
	if (!_.isUndefined(filter?.permissionFunction)) {
		if (!filter.permissionFunction(listParams)) {
			return [];
		}
	}
	if (!_.isUndefined(summaryDisplayItem)) {
		if (!summaryDisplayItem) return [];
	}
	const paramOptions = onMapOptionsV2(listParams, filterOptions, filter, summaryOptionsFlat, toggleOptions, toggleStates, summaryOptionsID, listParamsID);
	let selections = listParams[listParamsID];
	let reducedOptions = [];
	if (subSummaryList) {
		_.forEach(filters, subFilter => {
			const subOptions = subFilter.summaryItem ? onMapSummaryItems(subFilter, listParams, toggleStates, filterOptions) : [];
			reducedOptions = [...reducedOptions, ...subOptions];
		});
	} else if (summaryType == 'keywords_file') {
		reducedOptions = !_.isUndefined(selections?.name) || !_.isUndefined(selections) ? [`${summaryTitle} ${selections.name || selections}`] : [];
	} else if (summaryType=='file') {
		reducedOptions = !_.isUndefined(selections?.name) ? [selections.name] : [];
	} else if (selections && _.isArray(selections)) {
		if (summaryType=='comscore') {
			reducedOptions = _.reduce(listParams['comscore_segments'], (acc, opt) => {
				let newObj = { title: opt.name, values: _.map(opt.ids, o => o.name) };
				acc.push(newObj);
				return acc;
			}, [])
		} else if (summaryType == 'object') {
			if (listParamsID=='nlp_topics' || listParamsID=='exclude_nlp_topics' && (_.isArray(selections)&&!_.isObject(typeof selections[0]))) {
				reducedOptions = [selections.length];
			}
			reducedOptions = selections;
		} else if (summaryType == 'flatObject') {
			let initOptions = _.filter(paramOptions, opt => _.some(selections, selection => selection.id == opt.id));
			reducedOptions = _.reduce(selections, (acc, selection) => {
				let opt = _.find(initOptions, option => option[optionsIDKey]==selection[summaryFlatParentID]);
				let child = _.find(opt?.['children'], c => c[optionsIDKey] == selection[summaryFlatChildID]);
				if (opt&&child) {
					acc.push( {
						'parent': opt[optionsTitleKey],
						'child': child[optionsTitleKey]
					});
				}
				return acc;
			}, [])
		}
		else reducedOptions = _.filter(paramOptions, opt => _.includes(selections, opt[optionsIDKey]));
	} else if (selections && _.isObject(selections)) {
		if (summaryType == 'keywords') {
			reducedOptions = !_.isUndefined(selections?.name) ? [selections.name] : [''];
		}
		reducedOptions = _.reduce(paramOptions, (acc, opt) => {
			if (selections.hasOwnProperty(opt[optionsIDKey])) {
				const value = selections[opt[optionsIDKey]];
				if (_.includes(value, ',')) {
					const [min, max] = value.split(',');
					const title = listParams['segment_type']==0 ? opt.name : `${opt.name} (${min}, ${max})`;
					acc.push({
						id: opt[optionsIDKey],
						name: title
					});
				} else {
					const title = opt.title;
					const displayValue = `${title}: ${value}%`;
					acc.push({
						id: opt[optionsIDKey],
						title: displayValue
					});
				}
			}
			return acc;
		}, []);

	} else if ((!_.isNull(selections) && !_.isUndefined(selections))) {
		let selection = listParams[listParamsID];
		if (summaryType == 'keywords') {
			if (_.includes(summaryTitle, 'CSV')) selection = !_.isUndefined(selections?.name) ? selections.name : '';
			if (_.includes(summaryTitle, 'Threshold')) selection = `${summaryTitle} ${selection}`
			else {
				let option = _.find(filterOptions[optionsID], opt => opt[optionsIDKey]==selection);
				selection = `${summaryTitle} ${option?.[optionsTitleKey]}`;
			}
		} else if (_.includes(summaryType, 'input')) {
			if (summaryType=='input-minmax'&&selection) {
				const [min, max] = selection.split(', ');
				let formattedMin = _.isEmpty(min) ? '-' : formatBigNumber(min);
				let formattedMax = _.isEmpty(max) ? '-' : formatBigNumber(max);
				selection = `${title} (${formattedMin}, ${formattedMax})`;
			} else if ((selection || selection == 0) && summaryType=='input-percent') {
				selection = `${selection}%`;
			} else if (summaryOptionsID) {
				selection = paramOptions[optionsID];
			}

		} else if (paramsID=='brand_suitability') {
			const [min, max] = selection.split(',');
			selection = `(${min}, ${max})`;
		} else if (summaryOptionsID) {
			selection = _.find(filterOptions[optionsID], opt => opt[optionsIDKey]==selection);
		}
		reducedOptions = [ selection ];
	}
	return reducedOptions;
}

export const setJsonParamV2 = (key, value, jsonParams, listParams) => {
	if (_.includes(key, '.')) {
		const paramKeys = _.split(key, '.');
		if (jsonParams[paramKeys[0]]) {
			jsonParams[paramKeys[0]][paramKeys[1]] = value;
		} else {
			jsonParams[paramKeys[0]] = {};
			jsonParams[paramKeys[0]][paramKeys[1]] = value;
		}
	} else {
		if (key == 'advanced_suitability_categories'||key=='nlp_suitability_categories'&&_.isObject(value)) {
			let advancedSuitabilityCats = _.fromPairs(
				_.map(value, (val, key) => {
					const [min_score, max_score] = val.split(',').map(_.toNumber);
					return [key, { min_score, max_score }];
					})
				);
			 jsonParams['category_suitability'] = {
				'score_version':  key=='advanced_suitability_categories' ? 'v1' : 'v2',
				'categories': advancedSuitabilityCats
			}
		} else if (key == 'score_threshold'&&(_.isUndefined(jsonParams?.['advanced_suitability_categories']&&_.isUndefined(jsonParams['nlp_suitability_categories'])))) {
			let minMax = value.split(',');
			let standardSuitability = {
				'score_version': listParams?.nlp_suitability == 'nlp_suitability_categories' ? 'v2' : 'v1',
				'scores': {
					'min_score': Number(minMax[0]),
					'max_score': Number(minMax[1])
				}
			}
			jsonParams['basic_suitability'] = standardSuitability;
		} else {
			jsonParams[key] = value;
		}
	}
};

export const setJsonParam = (key, value, jsonParams) => {
	if (_.includes(key, '.')) {
		const paramKeys = _.split(key, '.');
		if (jsonParams[paramKeys[0]]) {
			jsonParams[paramKeys[0]][paramKeys[1]] = value;
		} else {
			jsonParams[paramKeys[0]] = {};
			jsonParams[paramKeys[0]][paramKeys[1]] = value;
		}
	} else {
		if (key == 'advanced_suitability_categories') {
			let advancedSuitabilityCats = _.fromPairs(
				_.map(value, (val, key) => {
					const [min_score, max_score] = val.split(',').map(_.toNumber);
					return [key, { min_score, max_score }];
					})
				);
			jsonParams['category_suitability'] = {
				'score_version': 'v1',
				'categories': advancedSuitabilityCats
			}
		} else if (key == 'score_threshold') {
			let minMax = value.split(',');
			let standardSuitability = {
				'score_version': 'v1',
				'scores': {
					'min_score': Number(minMax[0]),
					'max_score': Number(minMax[1])
				}
			}
			jsonParams['basic_suitability'] = standardSuitability;
		} else if (key == 'languages'){
			jsonParams['language_threshold_percentages'] = value;
		} else {
			jsonParams[key] = value;
		}
	}
};

export const onMapOptionsV2 = (listParams, filterOptions, filter, flat = false, toggleOptions=false, toggleStates=null, summaryOptionsID=false, listParamsID=null) => {
	const { options, optionsID, optionsIDDependant, toggles, optionsSelectionMethod, summaryParamsID, associatedToggleParent, optionsIDPrefix } = filter;
	if (flat) {
		if (toggleOptions) {
			let toggleParent = toggles[0].toggleParent;
			let currentToggleOption = toggleStates[toggleParent];
			if (optionsIDPrefix && _.includes(currentToggleOption, '3_0_2')) {
				return filterOptions[`${optionsIDPrefix}${currentToggleOption}_flat`];
			}
			return filterOptions[`${currentToggleOption}_flat`];
		}
		return filterOptions[`${optionsID}_flat`];
	} else if (optionsID && optionsIDDependant) {
		if (optionsIDDependant == 'segment_type') {
			const type = Segment.TYPES[listParams.segment_type] || 'none';
			let updatedOptionsID = optionsSelectionMethod(listParams, toggleStates);
			return filterOptions[`${type}${updatedOptionsID}`];
		}
		return filterOptions[`${listParams[optionsIDDependant]}${optionsID}`];
	} else if (optionsID) {
		if (_.includes(optionsID, '.')) {
			const objectIDs = optionsID.split('.');
			return filterOptions[objectIDs[0]][objectIDs[1]];
		} else if (toggleOptions){
			let toggleParent = toggles[0].toggleParent;
			let filterOptionID = toggleStates[toggleParent];
			if (optionsIDPrefix && _.includes(filterOptionID, '3_0_2')) {
				return filterOptions[`${optionsIDPrefix}${filterOptionID}`];
			}
			return filterOptions[filterOptionID];
		} else if (optionsSelectionMethod) {
			let newOptionsID = optionsSelectionMethod(listParams, toggleStates);
			return filterOptions[newOptionsID];
		}
		return filterOptions[optionsID];
	} else if (summaryOptionsID) {
		return filterOptions[listParamsID];
	} else if (summaryParamsID) {
		return filterOptions[toggleStates[associatedToggleParent]]
	}
	if (optionsIDPrefix) {
		return filterOptions[`${optionsIDPrefix}${optionsID}`];
	}
	return options;
};

export const onMapOptions = (listParams, filterOptions, filter, flat = false) => {
	const { options, optionsID, optionsIDDependant } = filter;
	if (flat) {
		return filterOptions[`${optionsID}_flat`];
	} else if (optionsID && optionsIDDependant) {
		if (optionsIDDependant == 'segment_type') {
			const type = Segment.TYPES[listParams.segment_type] || 'none';
			return filterOptions[`${type}${optionsID}`];
		}
		return filterOptions[`${listParams[optionsIDDependant]}${optionsID}`];
	} else if (optionsID) {
		if (_.includes(optionsID, '.')) {
			const objectIDs = optionsID.split('.');
			return filterOptions[objectIDs[0]]?.[objectIDs[1]];
		}
		return filterOptions[optionsID];
	}
	return options;
};

export const onPopulateDescription = (
	filter,
	descriptionType,
	listParams,
	toggleStates,
	filterOptions,
	stringified=true,
	steps,
	cardView=false
) => {
	const { filterType, paramsID, summaryMainFilter } = filter;
	let checkParamsID = paramsID;
	if (_.isArray(checkParamsID)) {
		checkParamsID = paramsID[0];
	}
	if (descriptionType=='platform_type_hack') { // AVP wanted this because we have now meta_prime instead of ig or fb on platform_type
		if (listParams.segment_type==201) return 'Facebook';
		return 'Instagram';
	} else if (descriptionType == 'count') {
		if (!_.isUndefined(summaryMainFilter)) {
			checkParamsID = summaryMainFilter;
		} else if (filterType == 'compound-toggle') {
			checkParamsID = toggleStates[paramsID];
			let filterToPopulate = _.find(filter.filters, ['toggleID', checkParamsID]);
			if (filterToPopulate?.summaryInclusion&&!_.isEmpty(steps)) {
				const inclusion = onPopulateDescription(filterToPopulate, descriptionType, listParams, toggleStates, filterOptions);
				const exclusionFilter = findPresetFilter(steps, filterToPopulate?.summaryInclusion);
				const exclusion = onPopulateDescription(exclusionFilter, descriptionType, listParams, toggleStates, filterOptions);
				if (_.isUndefined(inclusion)&&_.isUndefined(exclusion)) return undefined;
				return ({
					inclusion: _.isNumber(inclusion) ? inclusion : '-',
					exclusion: _.isNumber(exclusion) ? exclusion : '-'
				});
			} else if (filterToPopulate?.paramsID!==checkParamsID) {
				checkParamsID = filterToPopulate.paramsID;
			}
		} else if (filterType=='checkbox-threshold-input-selector') {
			return listParams[checkParamsID] ? Object.keys(listParams[checkParamsID])?.length > 0 ? Object.keys(listParams[checkParamsID])?.length : undefined : undefined;
		}
		let paramLength = _.isPlainObject(listParams[checkParamsID]) ? Object.keys(listParams[checkParamsID]).length : listParams[checkParamsID]?.length;
		return paramLength > 0 ? paramLength : undefined;
	} else if (descriptionType == 'input-number') {
		let value = listParams[paramsID];
		if (_.includes(value, ', ')) {
			const splitted = _.split(value, ', ');
			value = [];
			if (!_.isEmpty(splitted[0])) value.push(`Min ${thousandsSeparated(splitted[0])}`);
			if (!_.isEmpty(splitted[1])) value.push(`Max ${thousandsSeparated(splitted[1])}`);
			value = _.join(value, ' ');
		}
		return listParams[paramsID] ? value : undefined;
	} else if (descriptionType == 'selections') {
		let filterToPopulate = filter;
		if (filterType == 'compound-toggle') {
			checkParamsID = toggleStates[paramsID];
			filterToPopulate = _.find(filter.filters, ['toggleID', checkParamsID]);
			if (filterToPopulate?.summaryInclusion&&!_.isEmpty(steps)&&!cardView) {
				const inclusion = onPopulateDescription(filterToPopulate, descriptionType, listParams, toggleStates, filterOptions);
				const exclusionFilter = findPresetFilter(steps, filterToPopulate?.summaryInclusion);
				const exclusion = onPopulateDescription(exclusionFilter, descriptionType, listParams, toggleStates, filterOptions);
				return ({ inclusion, exclusion });
			}
		} else if (filterType == 'compound-vertical') {
			checkParamsID = filter['summaryMainFilter'];
			filterToPopulate = _.find(filter.filters, ['paramsID', checkParamsID]);
		} else if (filterType== 'checkbox-threshold-input-selector') {
			const { optionsIDKey, optionsTitleKey } = filterToPopulate;
			let mappedOptions = [];
			if (listParams[checkParamsID] && !_.isArray(listParams[checkParamsID])) {
				mappedOptions = Object.entries(listParams[checkParamsID]).map(([key, value]) => {
					const foundItem = _.find(filterOptions[checkParamsID], [optionsIDKey, key]);
					return foundItem ? `${foundItem[optionsTitleKey]}: ${value}%` : null;
				});
			} else {
				mappedOptions = _.map(listParams[checkParamsID], (id) => {
					const options = onMapOptions(listParams, filterOptions, filterToPopulate) || [];
					const itemIndex = _.findIndex(options, [optionsIDKey, id]);
					const foundItem = options[itemIndex] || {};
					return foundItem[optionsTitleKey];
				});
			}
			return mappedOptions;
		}
		if (!_.isEmpty(filterToPopulate)) {
			if (checkParamsID !== filterToPopulate?.paramsID && !_.isArray(filterToPopulate?.paramsID)) {
				checkParamsID = filterToPopulate.paramsID;
			}
			const { optionsIDKey, optionsTitleKey, selectionsTooltipItems, filterType } = filterToPopulate;
			if (filterType == 'checkbox-threshold-input-selector') {
				const { optionsIDKey, optionsTitleKey } = filterToPopulate;
				let mappedOptions = [];
				if (listParams[checkParamsID] && !_.isArray(listParams[checkParamsID])) {
					mappedOptions = Object.entries(listParams[checkParamsID]).map(([key, value]) => {
						const foundItem = _.find(filterOptions[checkParamsID], [optionsIDKey, key]);
						return foundItem ? `${foundItem[optionsTitleKey]}: ${value}%` : null;
					});
				} else {
					mappedOptions = _.map(listParams[checkParamsID], (id) => {
						const options = onMapOptions(listParams, filterOptions, filterToPopulate) || [];
						const itemIndex = _.findIndex(options, [optionsIDKey, id]);
						const foundItem = options[itemIndex] || {};
						return foundItem[optionsTitleKey];
					});
				}
				return mappedOptions;
			} else if (_.isArray(listParams[checkParamsID])) {
				if (selectionsTooltipItems == 'as-is') {
					if (stringified) {
						let stringValue = listParams[checkParamsID].join(', ');
						if (stringValue.length > 300) stringValue = `${stringValue.substring(0, 400)}...`;
						return stringValue;
					}
					return listParams[checkParamsID];
				} else if (selectionsTooltipItems == 'optionsTitleKey') {
					if (stringified) {
						let stringValue = listParams[checkParamsID].map((item) => item[optionsTitleKey]).join(', ');
						if (stringValue.length > 300) stringValue = `${stringValue.substring(0, 400)}...`;
						return stringValue;
					}
				} else if (selectionsTooltipItems == 'children-selections') {
					const options = _.flatten(
						_.map(onMapOptions(listParams, filterOptions, filterToPopulate), (parent) => {
							let returnValue = [parent];
							if (_.isArray(parent.children)) returnValue = [...returnValue, ...parent.children];
							return returnValue;
						})
					) || [];
					const mappedValues = _.map(listParams[checkParamsID], (id) => {
						const itemIndex = _.findIndex(options, [optionsIDKey, id]);
						const foundItem = options[itemIndex] || {};
						return foundItem[optionsTitleKey];
					});
					if (stringified) {
						let stringValue = mappedValues.join(', ');
						if (stringValue.length > 300) stringValue = `${stringValue.substring(0, 400)}...`;
						return stringValue;
					}
					return mappedValues;
				}
				const mappedValues = _.map(listParams[checkParamsID], (id) => {
					const options = onMapOptions(listParams, filterOptions, filterToPopulate) || [];
					const itemIndex = _.findIndex(options, [optionsIDKey, id]);
					const foundItem = options[itemIndex] || {};
					return foundItem[optionsTitleKey];
				});
				if (stringified) {
					let stringValue = mappedValues.join(', ');
					if (stringValue.length > 300) stringValue = `${stringValue.substring(0, 400)}...`;
					return stringValue;
				}
				return mappedValues;
			} else if (_.isNumber(listParams[checkParamsID]) || _.isString(listParams[checkParamsID])) {
				const options = onMapOptions(listParams, filterOptions, filterToPopulate) || [];
				const itemIndex = _.findIndex(options, [optionsIDKey, listParams[checkParamsID]]);
				const foundItem = options[itemIndex] || {};
				return foundItem[optionsTitleKey];
			}
		}
	} else if (descriptionType == 'boolean') {
		return listParams[paramsID] ? 'Yes' : 'No Preference';
	} else if (descriptionType == 'brand-suitability') {
		if (listParams.score_threshold&&toggleStates[paramsID]=='score_threshold'||listParams.score_threshold&&_.isEmpty(listParams.advanced_suitability_categories)) {
			return garmSuitabilityDescriptionMapping(listParams.score_threshold, listParams.filter_type == 'exclusion');
		} else if (listParams.advanced_suitability_categories&&toggleStates[paramsID]=='advanced_suitability_categories') {
			return `Advanced Suitability Content Selected (${Object.keys(listParams.advanced_suitability_categories).length})`;
		}
	} else if (descriptionType == 'brand-suitability-details'&&toggleStates[paramsID]=='advanced_suitability_categories') {
		let stringValue = _.map(
			mapGarmSuitability(
				listParams.advanced_suitability_categories,
				filterOptions.advanced_suitability_categories,
				true
			),
			(item) => item.title
		).join(', ');
		if (stringValue.length > 300) stringValue = `${stringValue.substring(0, 400)}...`;
		return stringValue;
	} else if (descriptionType=='file-name') {
		return listParams[filter.paramsID]?.name;
	}
	return undefined;
};

export const garmSuitabilityDescriptionMapping = (selection, exclusionType=false) => {
	const advancedSuitabilityStrings = {
		1: 'Allow Any Suitable Content',
		2: 'Allow Low Suitability Content',
		3: 'Allow Medium Suitability Content',
		4: 'Allow Only Suitable Content'
	};
	const minValue = selection.split(',')[0];
	const maxValue = selection.split(',')[1];
	const mapping = (minValue) => {
		if (minValue < 7) {
			return advancedSuitabilityStrings[1];
		} else if (minValue < 8){
			return advancedSuitabilityStrings[2];
		} else if (minValue < 9) {
			return advancedSuitabilityStrings[3];
		}
		return advancedSuitabilityStrings[4];
	}
	const exclusionMapping = (maxValue) => {
		return maxValue >= 9 ? advancedSuitabilityStrings[4] : maxValue >= 8 ? advancedSuitabilityStrings[3] : maxValue >= 7 ? advancedSuitabilityStrings[2] : advancedSuitabilityStrings[1];
	}

	return exclusionType ? exclusionMapping(maxValue) : mapping(minValue);
}

export const mapTemplateGarm = (selections, garm=false, exclusionType=false) => {
	const mapInclusionSelection = (input) => {
		return input == 1 ? '0,10' : input == 2 ? '7,10' : input == 3 ? '8,10' : '9,10'
	}
	const mapExclusionSelection = (input) => {
		return input == 1 ? '0,10' : input == 2 ? '0,9' : input == 3 ? '0,8' : '0,7'
	}
	let mappedSelection;
	if (!_.isArray(selections) && !_.isPlainObject(selections)) {
		mappedSelection = exclusionType ? mapExclusionSelection(selections) : mapInclusionSelection(selections);
	} else if (_.isPlainObject(selections)&&garm){
		mappedSelection = _.mapValues(selections, threshold => {
			return typeof threshold == 'string' ? threshold : `${threshold.min_score},${threshold.max_score}`;
		});
	} else if (_.isPlainObject(selections)){
		return `${selections.min_score},${selections.max_score}`;
	} else {
		mappedSelection = _.reduce(selections, (result, selection) => {
			result[selection.category_id] = exclusionType ? mapExclusionSelection(selections) : mapInclusionSelection(selection.score_threshold);
			return result
		}, {});
	}
	return mappedSelection;
}

export const mapGarmSuitability = (selections, options, exclusionType=false) => {
	const advancedSuitabilityStrings = {
		1: 'Allow Any Suitable Content',
		2: 'Allow Low Suitability Content',
		3: 'Allow Medium Suitability Content',
		4: 'Allow Only Suitable Content'
	};
	const mapping = (minValue) => {
		if (minValue < 7) {
			return advancedSuitabilityStrings[1];
		} else if (minValue < 8){
			return advancedSuitabilityStrings[2];
		} else if (minValue < 9) {
			return advancedSuitabilityStrings[3];
		}
		return advancedSuitabilityStrings[4];
	}
	const exclusionMapping = (maxValue) => {
		return maxValue >= 9 ? advancedSuitabilityStrings[4] : maxValue >= 8 ? advancedSuitabilityStrings[3] : maxValue >= 7 ? advancedSuitabilityStrings[2] : advancedSuitabilityStrings[1];
	}
	if (!_.isPlainObject(selections)) {
		return _.map(selections, (item) => {
			const advancedCategory = _.find(options, ['id', Number(item.category_id)]);
			const suitability = advancedSuitabilityStrings[item.score_threshold];
			return { id: _.random(0, 100), title: advancedCategory?.name || 'Not Available', description: suitability };
		});
	}
	return _.map(selections, (minMax, id) => {
		const advancedCategory = _.find(options, opt => String(opt.id) === String(id));
		const suitability = exclusionType ? exclusionMapping(minMax.max_score) : mapping(minMax.min_score);
		return { id: _.random(0, 100), title: advancedCategory?.name || 'Not Available', description: suitability };
	});
};

// Forecasting
export const mapForecastingGraphData = (data) => {
	const apiData = !_.isEmpty(data[0]?.trend) ? data[0]?.trend : [];
	const formatTrendData = (trend) => {
		const monthData = {};
		const monthOrder = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December'
		];
		trend.forEach((yearObj) => {
			const year = yearObj.label;
			const months = yearObj.value;
			for (const monthKey in months) {
				const month = months[monthKey];
				const monthLabel = month.label;
				if (!monthData[monthLabel]) {
					monthData[monthLabel] = { name: monthLabel, Q: month.quarter };
				}
				monthData[monthLabel][year] = month.value;
				const quarterKey = `${year}_${month.quarter}`;
				monthData[monthLabel][quarterKey] = month.quarter_average;
				if (!monthData[monthLabel].Q) {
					monthData[monthLabel].Q = month.quarter;
				}
			}
		});
		const formattedData = Object.values(monthData);
		formattedData.sort((a, b) => monthOrder.indexOf(a.name) - monthOrder.indexOf(b.name));
		return formattedData;
	};
	const getAvailableYears = (trend) => {
		return trend.map((yearObj) => yearObj.label);
	};
	const chartKeys =  getAvailableYears(apiData);
	const chartLineColors = ['#E0301E','#0089EB','#68D391','#FFD979'];
	const getChartColors = (keys) => {
		return _.reduce(keys, (acc, key, i) => {
			acc[key] = chartLineColors[i];
			return acc;
		}, {});
	}
	return {
		keys: chartKeys,
		graphData: formatTrendData(apiData),
		yearsColors: getChartColors(chartKeys)
	};
};

export const getFilterTitle = (filter, listParams, toggleStates) => {
	if (filter.filters) {
		let mainTitle = filter.title;
		_.each(filter.filters, item => {
			if (toggleStates[filter.paramsID]==item.toggleID&&item.secondaryTitle) {
				mainTitle = item.secondaryTitle;
			}
		});
		return mainTitle;
	}
	return filter.cardTitle ? filter.cardTitle : filter.title;
}

export const extendClasses = (classes) => {
	class Bases {
        constructor() {
          classes.forEach(base => Object.assign(this, new base()));
        }
      }
      classes.forEach(base => {
        Object.getOwnPropertyNames(base.prototype)
        .filter(prop => prop != 'constructor')
        .forEach(prop => Bases.prototype[prop] = base.prototype[prop])
      })
      return Bases;
}

export const findTitleKey = (array) => {
	const displayKeys = ['title', 'name', 'value'];
	const firstItem = array[0];
	const displayKey = displayKeys.find(key => key in firstItem);
	return displayKey;
}

export const extractErrorKey = (errorMessage) => {
	let errorKeys = Object.keys(errorMessage.json);
	_.forEach(errorKeys, key => {
		errorMessage.json[key] =`${errorMessage.json[key]}: ${key}`
	})
	return errorMessage;
}

export const cleanCategoryValue = (item) =>
	item?.parent && item?.value && item.value.startsWith(`${item.parent}>`)
		? item.value.slice(item.parent.length + 1)
		: item?.value;

export const getCategoryNamesFromItem = (type, categoryIDs, categories, searchedIDs = [], primaryID = null) => {
	const names = categoryIDs
		.filter(id => _.isEmpty(searchedIDs) || _.includes(searchedIDs, `${id}`))
		.map(id => cleanCategoryValue(_.find(categories, ['id', id])))
		.filter(Boolean);
	if (primaryID !== null) {
		const primary = type === 'yt'
			? _.find(categories, ['id', primaryID])
			: _.find(categories, ['value', primaryID]);
		const primaryTitle = cleanCategoryValue(primary);
		return primaryTitle
			? [primaryTitle, ...names.filter(name => name !== primaryTitle)]
			: names;
	}
	return names;
};

export const formatItemsCategories = (type, items, categories, selectedIDs) => {
	if (!categories) return [...items];
	return items.map(item => {
		const { general_data } = item || {};
		if (!_.isEmpty(general_data?.iab_categories_3)) {
			const itemCategories = general_data.iab_categories_3;
			const primaryCategory = general_data?.primary_category_3;
			general_data.iab_categories_3 = getCategoryNamesFromItem(type,itemCategories,categories,selectedIDs,primaryCategory);
		}
		return item;
	});
};

/**
 * Maps language codes from overall_language_percentages to language names using language options
 *
 * @param {Array} items - Array of items containing general_data with overall_language_percentages
 * @param {Array} languageOptions - Array of language options with id and title properties
 * @returns {Array} - Array of items with formatted language data
 *
 * @example
 * const items = [{
 *   general_data: {
 *     overall_language_percentages: { "he": 100, "en": 50 }
 *   }
 * }];
 * const languageOptions = [
 *   { id: "he", title: "Hebrew" },
 *   { id: "en", title: "English" }
 * ];
 * const result = formatItemsLanguages(items, languageOptions);
 * // Result: items with general_data.languages and general_data.main_language added
 */
export const formatItemsLanguages = (items, languageOptions) => {
	if (!languageOptions || !Array.isArray(languageOptions)) return [...items];

	// Create a mapping object from language options for faster lookup
	const languageMap = _.reduce(languageOptions, (acc, lang) => {
		acc[lang.id] = lang.title;
		return acc;
	}, {});

	return items.map(item => {
		const { general_data } = item || {};
		if (general_data?.overall_language_percentages) {
			// Map language codes to language names and create array of language objects with percentages
			const languageObjects = _.reduce(general_data.overall_language_percentages, (acc, percentage, code) => {
				if (percentage > 0) {
					acc.push({
						code,
						name: languageMap[code] || code, // fallback to code if not found
						percentage
					});
				}
				return acc;
			}, []);

			// Create array of language names (strings)
			const languages = languageObjects.map(lang => lang.name);

			// Add the formatted language data to general_data
			general_data.languages = languages;

			// Add the main language (highest percentage)
			general_data.main_language = _.maxBy(languageObjects, lang => lang.percentage)?.name || null;
		}
		return item;
	});
};

/**
 * Modern React hook for detecting clicks outside a component
 * Compatible with React 17+ and works with refs instead of element IDs
 *
 * @param {Object} ref - React ref object pointing to the element to monitor
 * @param {Function} handler - Callback function to execute when clicking outside
 * @param {Array} dependencies - Optional dependencies array for the effect
 * @returns {void}
 *
 * @example
 * // In a functional component
 * const popoverRef = useRef(null);
 * useClickOutside(popoverRef, () => setShowPopover(false));
 *
 * return <div ref={popoverRef}>Popover content</div>;
 */
export const useClickOutside = (ref, handler, dependencies = []) => {
	React.useEffect(() => {
		const listener = (event) => {
			// Do nothing if clicking ref's element or descendent elements
			if (!ref.current || ref.current.contains(event.target)) {
				return;
			}

			// If clicking on a popover opener, check if it's for a different popover
			const popoverOpener = event.target.closest('.popover-opener');
			if (popoverOpener) {
				const targetPopoverId = popoverOpener.getAttribute('data-popover-id');
				const currentPopoverId = ref.current?.id;

				// Close if clicking on a different popover opener OR the same opener (to toggle)
				if (targetPopoverId && targetPopoverId !== currentPopoverId) {
					handler(event);
					return;
				} else if (targetPopoverId === currentPopoverId) {
					// Same popover opener - let the component handle the toggle
					return;
				}
			}

			handler(event);
		};

		// Use mousedown and touchstart for better mobile support
		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler, ...dependencies]);
};

/**
 * Legacy click outside handler for class components (deprecated)
 * @deprecated Use useClickOutside hook instead for better React 17 compatibility
 * @param {string} elementId - The ID of the element to check if clicked outside
 * @param {Function} onOutsideClick - Callback function to execute when clicking outside
 * @param {Object} options - Optional configuration
 * @returns {Object} - Object with addListener and removeListener methods
 */
export const createClickOutsideHandler = (elementId, onOutsideClick, options = {}) => {
	const { useContains = true, useRecursiveCheck = true } = options;

	let bodyClickHandler = null;

	const checkRecursively = (offsetParent, id) => {
		if (!offsetParent?.id) return false;
		if (offsetParent.id === id || offsetParent.id.includes(id)) return true;
		if (!_.isEmpty(offsetParent.offsetParent)) {
			return checkRecursively(offsetParent.offsetParent, id);
		}
		return false;
	};

	const addListener = () => {
		if (bodyClickHandler) return; // Prevent duplicate listeners

		bodyClickHandler = function(event) {
			const targetElement = useContains
				? document.getElementById(elementId)
				: document.querySelector(`#${elementId}`);

			if (!targetElement) return;

			const isClickInside = targetElement.contains(event.target);
			const isClickInParent = useRecursiveCheck && checkRecursively(event.target.offsetParent, elementId);
			const hasValidOffsetParent = event.target.offsetParent !== null;

			if (!isClickInside && !isClickInParent && hasValidOffsetParent) {
				onOutsideClick();
			}
		};

		// Use setTimeout to avoid React 17 event delegation issues
		setTimeout(() => {
			document.addEventListener('click', bodyClickHandler);
		}, 0);
	};

	const removeListener = () => {
		if (bodyClickHandler) {
			document.removeEventListener('click', bodyClickHandler);
			bodyClickHandler = null;
		}
	};

	return {
		addListener,
		removeListener
	};
};

/**
 * Normalizes user-typed duration into HHH:MM:SS. Accepts colon-separated or raw digits.
 * @param {string} raw - Colon-separated ("1:30:00") or raw digits ("13000").
 * @returns {string} HHH:MM:SS formatted string, or empty string if invalid.
 */
export const formatDurationInput = raw => {
	if (!raw && raw !== 0) return '';
	const str = String(raw).trim();
	if (!str) return '';

	let h, m, s;

	if (str.includes(':')) {
		const parts = str.split(':').slice(0, 3);
		while (parts.length < 3) parts.unshift('0');
		h = parseInt(parts[0], 10) || 0;
		m = parseInt(parts[1], 10) || 0;
		s = parseInt(parts[2], 10) || 0;
	} else {
		// Raw digits: right-align as HHMMSS (e.g. "1234" → "001234" → 00h 12m 34s)
		const digits = str.replace(/\D/g, '');
		if (!digits) return '';
		const padded = digits.padStart(6, '0');
		h = parseInt(padded.slice(0, -4), 10) || 0;
		m = parseInt(padded.slice(-4, -2), 10) || 0;
		s = parseInt(padded.slice(-2), 10) || 0;
	}

	// Carry overflow from seconds → minutes → hours
	const secCarry = Math.floor(s / 60);
	s = s % 60;
	m = m + secCarry;
	const minCarry = Math.floor(m / 60);
	m = m % 60;
	h = h + minCarry;

	if (h > 999) h = 999;

	const hStr = String(h).padStart(3, '0');
	return `${hStr}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Converts any duration input to total seconds.
 * @param {string} value - Colon-separated ("1:30:00") or raw digits ("13000").
 * @returns {number|undefined} Total seconds, or undefined if invalid.
 */
export const parseDurationToSeconds = value => {
	const formatted = formatDurationInput(value);
	if (!formatted) return undefined;
	const [h, m, s] = formatted.split(':').map(Number);
	return h * 3600 + m * 60 + s;
};

/**
 * Validates raw duration input.
 * Checks structure (digits-only, max 3 colon-separated parts) and
 * rejects out-of-range minutes/seconds (> 59) in colon-separated input.
 * @param {string} value - Colon-separated ("1:30:00") or raw digits ("13000").
 * @returns {string|null} Error message if invalid, or null if valid.
 */
export const validateDuration = value => {
	if (!value) return null;
	const str = String(value).trim();
	if (!str) return null;

	const invalid = 'Please enter a valid format (e.g. HHH:MM:SS).';

	if (str.includes(':')) {
		const parts = str.split(':');
		if (parts.length > 3) return invalid;
		if (parts.some(p => !/^\d+$/.test(p))) return invalid;
		const nums = parts.map(Number);
		if (parts.length === 3) {
			if (parts[0].length > 3) return 'Hours must not exceed 3 digits.';
			if (nums[2] > 59) return 'Seconds must not exceed 59.';
			if (nums[1] > 59) return 'Minutes must not exceed 59.';
		}
		if (parts.length === 2) {
			if (nums[1] > 59) return 'Seconds must not exceed 59.';
			if (nums[0] > 59) return 'Minutes must not exceed 59.';
		}
	} else {
		if (!/^\d+$/.test(str)) return invalid;
		if (str.length > 7) return 'Hours must not exceed 3 digits.';
	}

	const formatted = formatDurationInput(str);
	if (!formatted) return invalid;

	return null;
};

/**
 * Cross-validates that expectedMin does not exceed expectedMax.
 * @param {string} expectedMin - Min duration (e.g. "1:30:00", "13000").
 * @param {string} expectedMax - Max duration (e.g. "2:00:00", "20000").
 * @returns {string|null} Error message if min exceeds max, or null if valid.
 */
export const validateDurationRange = (expectedMin, expectedMax) => {
	const minSec = parseDurationToSeconds(expectedMin);
	const maxSec = parseDurationToSeconds(expectedMax);
	if (minSec !== undefined && maxSec !== undefined && minSec > maxSec) {
		return 'Wrong values! The minimum cannot exceed the maximum. ';
	}
	return null;
};
